import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';

import {
  BatteryLow,
  BatteryFull,
  BatteryHalf,
  BatteryEmpty,
  phxWebStyleTokens,
} from 'phx-core';

import { IBattery, IBatteryStatus, IAGModelAndBattery, IBatteryTooltip } from '@app/misc/battery-status.component';
import { ASSET_POWER_MODES } from '@lib/asset-power-modes';

import { AG_MODELS } from './asset-gateway.consts';
import { IAssetGateway, IKeyTypeBooleans } from './asset-gateway.interface';
import { IKeyTypeStrings } from '@app/mobile/devices/asset-gateway/installation/asset-gateway-helpers';

enum BatteryStatuses {
  EMPTY = 'Empty',
  EMPTY_CABLED = 'Empty',
  LOW_BATTERY_CABLED = 'Low',
  LOW_BATTERY = 'Low Battery',
  HALF_BATTERY = 'Half Battery',
  FULL_BATTERY = 'Full Battery',
  EMPTY_BATTERY = 'Empty Battery',
  OUT_OF_BATTERY = 'Out of Battery',
  CRITICAL_BATTERY_CABLED = 'Critical',
  CRITICAL_BATTERY = 'Critical Battery',
}

enum BatteryIcons {
  BATTERY_LOW = 'battery-low.svg',
  BATTERY_FULL = 'battery-full.svg',
  BATTERY_HALF = 'battery-half.svg',
  OUT_OF_BATTERY = 'out-of-battery.svg',
  BATTERY_LOW_BLINKING = 'battery-low-blinking.svg',
}

enum OldBatteryPercentagesOfAG55 {
  OUT_OF_BATTERY = 1,
  BATTERY_LOW = 35,
  BATTERY_HALF = 76,
}

enum BatteryPercentageOfAG55 {
  OUT_OF_BATTERY = 30,
  BATTERY_LOW_BLINKING = 50,
  BATTERY_LOW = 60,
  BATTERY_HALF = 70,
  FULL_BATTERY = 100,
}

enum BatteryPercentageOfAG31 {
  OUT_OF_BATTERY = 1,
  BATTERY_LOW_BLINKING = 11,
  BATTERY_LOW = 15,
  BATTERY_HALF = 76,
  FULL_BATTERY = 100,
}

const AG31LowBatteryLinks: { [key: string]: string } = {
  lowBattery: 'lowBatteryLink',
  criticalBattery: 'criticalBatteryLink',
};
const MINUTES_IN_ONE_HOUR: number = 60;
const ICON_SIZE_16: string = '16x16';
const FV_BATTERY_ICON_16: string = '/fleet-view/icon-16-o-';
const FV_BATTERY_ICON_20: string = '/fleet-view/icon-20-o-';
const ICON_COLOR_RED: string = phxWebStyleTokens.colorContentNegative;
const ICON_COLOR_GRAY: string = phxWebStyleTokens.colorContentSubdued;

const BATTERY_STATUSES: { [key: string]: string } = {
  [BatteryStatuses.EMPTY]: $localize`Empty`,
  [BatteryStatuses.EMPTY_BATTERY]: $localize`Empty Battery`,
  [BatteryStatuses.HALF_BATTERY]: $localize`Half`,
  [BatteryStatuses.FULL_BATTERY]: $localize`Full`,
  [BatteryStatuses.LOW_BATTERY]: $localize`Low battery`,
  [BatteryStatuses.OUT_OF_BATTERY]: $localize`Out of battery`,
  [BatteryStatuses.CRITICAL_BATTERY]: $localize`Critical battery`,
};

const BatteryTooltipTexts: { [key: string]: string } = {
  [BatteryStatuses.LOW_BATTERY]: $localize`Low Battery`,
  [BatteryStatuses.CRITICAL_BATTERY]: $localize`Critical Battery`,
  [BatteryStatuses.HALF_BATTERY]: $localize`Asset Gateway Mini Battery: Half`,
  [BatteryStatuses.FULL_BATTERY]: $localize`Asset Gateway Mini Battery: Full`,
  [BatteryStatuses.OUT_OF_BATTERY]: $localize`Asset Gateway Mini Battery: Empty`,
};

const AG55LowBatteryTooltipTexts: { [key: string]: string } = {
  [BatteryStatuses.EMPTY]: $localize`Empty`,
  [BatteryStatuses.LOW_BATTERY_CABLED]: $localize`Low`,
  [BatteryStatuses.LOW_BATTERY]: $localize`Low Battery`,
  [BatteryStatuses.CRITICAL_BATTERY_CABLED]: $localize`Critical`,
  [BatteryStatuses.CRITICAL_BATTERY]: $localize`Critical Battery`,
  [BatteryStatuses.HALF_BATTERY]: $localize`Asset Gateway Battery: Half`,
  [BatteryStatuses.FULL_BATTERY]: $localize`Asset Gateway Battery: Full`,
};
const batteryLevelText: string = $localize`Battery Level`;
const ASSET_GATEWAY_BATTERY_TEXT: string = $localize`Asset Gateway Battery`;
const EVERY_12_HOURS_TEXT: string = $localize`Every 12 hours`;

const AG55OutOfBatteryText: string = $localize`The Asset Gateway is not operational. Ensure the device has a direct view of the sun or use a cable to charge the battery to avoid missing updates from this asset.`;

const AG55LowBatteryText: string = $localize`Ensure the device has a direct view of the sun or use a cable to charge the battery to avoid missing updates from this asset.`;

const AG55CriticalBatteryText: string = $localize`The Asset Gateway is operating at a lower check-in rate of once every 12 hours. Ensure the device has a direct view of the sun or use a cable to charge the battery to avoid missing updates from this asset.`;

const AG55LowBatteryMessages: IKeyTypeStrings = {
  LOW_BATTERY : AG55LowBatteryText,
  OUT_OF_BATTERY : AG55OutOfBatteryText,
  CRITICAL_BATTERY : AG55CriticalBatteryText,
};

const AG55LowBatteryLinks: IKeyTypeStrings = {
  LOW_BATTERY : 'notAvailable',
  OUT_OF_BATTERY : 'notAvailable',
  CRITICAL_BATTERY : 'notAvailable',
};

const AG31BatteryCriticalText: string =
  $localize`The Asset Gateway Mini is operating at a lower check-in rate. Replace the battery to avoid missing updates from this asset.`;
const AG31BatteryLowText: string =
  $localize`Replace the battery on the Asset Gateway Mini to avoid missing updates from this asset.`;
const AG_MODELS_LENGTH: number = 5;

function isModelAG31(assetGateway: IAssetGateway): boolean {
  return !!assetGateway.model?.startsWith(AG_MODELS.AG_31);
}

function isModelAG55(assetGateway: IAssetGateway): boolean {
  return !!assetGateway.model?.startsWith(AG_MODELS.AG_55);
}

function getOnlyAssetGatewayModel(model: string): string {
  return model.slice(0, AG_MODELS_LENGTH);
}

function convertAG31UpdateRateValue(value: number): number {
  return value / MINUTES_IN_ONE_HOUR;
}

function isBatteryPercentageValid(batteryCapacity: number): boolean {
  return !isNil(batteryCapacity) && !isNaN(batteryCapacity) && batteryCapacity <= 100 && batteryCapacity >= 0;
}

function isAG55BatteryPercentageEmpty(batteryCapacity: number): boolean {
  return batteryCapacity <= BatteryPercentageOfAG55.OUT_OF_BATTERY;
}

function isAG55BatteryPercentageCritical(batteryCapacity: number): boolean {
  return batteryCapacity > BatteryPercentageOfAG55.OUT_OF_BATTERY &&
    batteryCapacity <= BatteryPercentageOfAG55.BATTERY_LOW_BLINKING;
}

function isAG31BatteryPercentageCritical(batteryCapacity: number): boolean {
  return batteryCapacity > BatteryPercentageOfAG31.OUT_OF_BATTERY &&
    batteryCapacity < BatteryPercentageOfAG31.BATTERY_LOW_BLINKING;
}

function isAG55BatteryPercentageLow(batteryCapacity: number): boolean {
  return batteryCapacity > BatteryPercentageOfAG55.BATTERY_LOW_BLINKING &&
    batteryCapacity <= BatteryPercentageOfAG55.BATTERY_LOW;
}

function isAG31BatteryPercentageLow(batteryCapacity: number): boolean {
  return batteryCapacity >= BatteryPercentageOfAG31.BATTERY_LOW_BLINKING &&
    batteryCapacity < BatteryPercentageOfAG31.BATTERY_LOW;
}

function getAG55BatteryStatusFirstLabel(batteryLabel: string, shouldGetLabelOnly: boolean = false): string {
  let label: string;
  if (batteryLabel.includes(BatteryStatuses.EMPTY)) {
    label = shouldGetLabelOnly ? BATTERY_STATUSES[BatteryStatuses.EMPTY] : BATTERY_STATUSES[BatteryStatuses.EMPTY_BATTERY];
  } else if (batteryLabel.includes((BatteryStatuses.FULL_BATTERY).split(' ')[0])) {
    label = BATTERY_STATUSES[BatteryStatuses.FULL_BATTERY];
  } else if (batteryLabel.includes((BatteryStatuses.HALF_BATTERY).split(' ')[0])) {
    label = BATTERY_STATUSES[BatteryStatuses.HALF_BATTERY];
  } else if (batteryLabel.includes((BatteryStatuses.LOW_BATTERY).split(' ')[0])) {
    label = shouldGetLabelOnly ?
      BATTERY_STATUSES[BatteryStatuses.LOW_BATTERY].split(' ')[0] :
      startCase(BATTERY_STATUSES[BatteryStatuses.LOW_BATTERY])
    ;
  } else if (batteryLabel.includes((BatteryStatuses.CRITICAL_BATTERY).split(' ')[0])) {
    label = shouldGetLabelOnly ?
      BATTERY_STATUSES[BatteryStatuses.CRITICAL_BATTERY].split(' ')[0] :
      startCase(BATTERY_STATUSES[BatteryStatuses.CRITICAL_BATTERY])
    ;
  }
  return label;
}

function getTooltipContent(
  isReferenceLinkAvailable: boolean,
  referenceLink: string,
  nonTranslatedLabel: BatteryStatuses,
  tooltipContent: string,
  translatedlabelText: string,
): IBatteryTooltip {
  return {
    link: referenceLink,
    content: tooltipContent,
    label: nonTranslatedLabel,
    labelText: translatedlabelText,
    isLinkAvailable: isReferenceLinkAvailable,
  };
}

function getAG55TooltipContents(batteryLabel: BatteryStatuses, powerMode: ASSET_POWER_MODES): IBatteryTooltip {
  // For Cable Powered Assets - Do not Show Content and Learn More Button
  let tooltipContents: IBatteryTooltip = null;

  if (batteryLabel === BatteryStatuses.EMPTY) {
    if (powerMode === ASSET_POWER_MODES.CABLE_POWERED) {
      tooltipContents = getTooltipContent(
        false, '', BatteryStatuses.EMPTY_CABLED, null, AG55LowBatteryTooltipTexts[BatteryStatuses.EMPTY],
      );
    } else {
      tooltipContents = getTooltipContent(
        false, '', BatteryStatuses.EMPTY, AG55OutOfBatteryText, AG55LowBatteryTooltipTexts[BatteryStatuses.EMPTY],
      );
    }
  }

  if (batteryLabel === BatteryStatuses.CRITICAL_BATTERY) {
    if (powerMode === ASSET_POWER_MODES.CABLE_POWERED) {
      tooltipContents = getTooltipContent(
        false, '', BatteryStatuses.CRITICAL_BATTERY_CABLED, null,
        AG55LowBatteryTooltipTexts[BatteryStatuses.CRITICAL_BATTERY_CABLED],
      );
    } else {
      tooltipContents = getTooltipContent(
        false, '', BatteryStatuses.CRITICAL_BATTERY,
        AG55CriticalBatteryText, AG55LowBatteryTooltipTexts[BatteryStatuses.CRITICAL_BATTERY],
      );
    }
  }

  if (batteryLabel === BatteryStatuses.LOW_BATTERY) {
    if (powerMode === ASSET_POWER_MODES.CABLE_POWERED) {
      tooltipContents = getTooltipContent(
        false, '', BatteryStatuses.LOW_BATTERY_CABLED, null, AG55LowBatteryTooltipTexts[BatteryStatuses.LOW_BATTERY_CABLED],
      );
    } else {
      tooltipContents = getTooltipContent(
        false, '', BatteryStatuses.LOW_BATTERY, AG55LowBatteryText, AG55LowBatteryTooltipTexts[BatteryStatuses.LOW_BATTERY],
      );
    }
  }

  return tooltipContents;
}

function getNewAG55BatteryIconAndLabel(batteryCapacity: number, assetGatewayPowerMode: ASSET_POWER_MODES): IBatteryStatus {
  let iconName: string;
  let phxIconColor: string;
  let batteryLabel: string = '';
  let tooltipContents: IBatteryTooltip = null;
  let batteryAndTooltipData: IBatteryStatus = null;

  if (batteryCapacity <= BatteryPercentageOfAG55.OUT_OF_BATTERY) {
    iconName = BatteryEmpty;
    phxIconColor = ICON_COLOR_RED;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.EMPTY];
    tooltipContents = getAG55TooltipContents(BatteryStatuses.EMPTY, assetGatewayPowerMode);
  } else if (batteryCapacity <= BatteryPercentageOfAG55.BATTERY_LOW_BLINKING) {
    iconName = BatteryIcons.BATTERY_LOW_BLINKING;
    phxIconColor = ICON_COLOR_RED;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.CRITICAL_BATTERY];
    tooltipContents = getAG55TooltipContents(BatteryStatuses.CRITICAL_BATTERY, assetGatewayPowerMode);
  } else if (batteryCapacity <= BatteryPercentageOfAG55.BATTERY_LOW) {
    iconName = BatteryLow;
    phxIconColor = ICON_COLOR_RED;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.LOW_BATTERY];
    tooltipContents = getAG55TooltipContents(BatteryStatuses.LOW_BATTERY, assetGatewayPowerMode);
  } else if (batteryCapacity <= BatteryPercentageOfAG55.BATTERY_HALF) {
    iconName = BatteryHalf;
    phxIconColor = ICON_COLOR_GRAY;
    batteryLabel = AG55LowBatteryTooltipTexts[BatteryStatuses.HALF_BATTERY];
    tooltipContents = {
      label: BatteryStatuses.HALF_BATTERY,
      labelText: AG55LowBatteryTooltipTexts[BatteryStatuses.HALF_BATTERY],
    };
  } else if (batteryCapacity <= BatteryPercentageOfAG55.FULL_BATTERY) {
    iconName = BatteryFull;
    phxIconColor = ICON_COLOR_GRAY;
    batteryLabel = AG55LowBatteryTooltipTexts[BatteryStatuses.FULL_BATTERY];
    tooltipContents = {
      label: BatteryStatuses.FULL_BATTERY,
      labelText: AG55LowBatteryTooltipTexts[BatteryStatuses.FULL_BATTERY],
    };
  } else {
    iconName = '';
    phxIconColor = '';
    batteryLabel = null;
    tooltipContents = null;
  }

  batteryAndTooltipData = {
    tooltipData: tooltipContents,
    batteryData: { iconPath: iconName, iconColor: phxIconColor, label: batteryLabel },
  };
  return batteryAndTooltipData;
}

function getAG55BatteryIconAndLabel(batteryCapacity: number): IBattery {
  let iconName: string = '';
  let phxIconColor: string = '';
  let batteryLabel: string = '';

  if (batteryCapacity < OldBatteryPercentagesOfAG55.OUT_OF_BATTERY) {
    iconName = BatteryEmpty;
    phxIconColor = ICON_COLOR_RED;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.OUT_OF_BATTERY];
  } else if (batteryCapacity < OldBatteryPercentagesOfAG55.BATTERY_LOW) {
    iconName = BatteryLow;
    phxIconColor = ICON_COLOR_RED;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.LOW_BATTERY];
  } else if (batteryCapacity < OldBatteryPercentagesOfAG55.BATTERY_HALF) {
    iconName = BatteryHalf;
    phxIconColor = ICON_COLOR_GRAY;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.HALF_BATTERY];
  } else {
    iconName = BatteryFull;
    phxIconColor = ICON_COLOR_GRAY;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.FULL_BATTERY];
  }
  return { iconPath: iconName, iconColor: phxIconColor, label: batteryLabel };
}

function getAG31BatteryIconAndLabel(batteryCapacity: number): IBatteryStatus {
  let iconName: string;
  let phxIconColor: string;
  let batteryLabel: string = '';
  let tooltipContents: IBatteryTooltip = null;
  let batteryAndTooltipData: IBatteryStatus = null;

  if (batteryCapacity < BatteryPercentageOfAG31.OUT_OF_BATTERY) {
    iconName = BatteryEmpty;
    phxIconColor = ICON_COLOR_RED;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.OUT_OF_BATTERY];
    tooltipContents = {
      label: BatteryStatuses.OUT_OF_BATTERY,
      labelText: BatteryTooltipTexts[BatteryStatuses.OUT_OF_BATTERY],
    };
  } else if (batteryCapacity < BatteryPercentageOfAG31.BATTERY_LOW_BLINKING) {
    iconName = BatteryIcons.BATTERY_LOW_BLINKING;
    phxIconColor = ICON_COLOR_RED;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.CRITICAL_BATTERY];
    tooltipContents = {
      isLinkAvailable: false,
      link: 'noLinkAvailable',
      content: AG31BatteryCriticalText,
      label: BatteryStatuses.CRITICAL_BATTERY,
      labelText: BatteryTooltipTexts[BatteryStatuses.CRITICAL_BATTERY],
    };
  } else if (batteryCapacity < BatteryPercentageOfAG31.BATTERY_LOW) {
    iconName = BatteryLow;
    phxIconColor = ICON_COLOR_RED;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.LOW_BATTERY];
    tooltipContents = {
      isLinkAvailable: false,
      link: 'noLinkAvailable',
      content: AG31BatteryLowText,
      label: BatteryStatuses.LOW_BATTERY,
      labelText: BATTERY_STATUSES[BatteryStatuses.LOW_BATTERY],
    };
  } else if (batteryCapacity < BatteryPercentageOfAG31.BATTERY_HALF) {
    iconName = BatteryHalf;
    phxIconColor = ICON_COLOR_GRAY;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.HALF_BATTERY];
    tooltipContents = {
      label: BatteryStatuses.HALF_BATTERY,
      labelText: BatteryTooltipTexts[BatteryStatuses.HALF_BATTERY],
    };
  } else if (batteryCapacity <= BatteryPercentageOfAG31.FULL_BATTERY) {
    iconName = BatteryFull;
    phxIconColor = ICON_COLOR_GRAY;
    batteryLabel = BATTERY_STATUSES[BatteryStatuses.FULL_BATTERY];
    tooltipContents = {
      label: BatteryStatuses.FULL_BATTERY,
      labelText: BatteryTooltipTexts[BatteryStatuses.FULL_BATTERY],
    };
  } else {
    iconName = '';
    batteryLabel = null;
    tooltipContents = null;
  }
  batteryAndTooltipData = {
    tooltipData: tooltipContents,
    batteryData: { iconPath: iconName, iconColor: phxIconColor, label: batteryLabel },
  };
  return batteryAndTooltipData;
}

function getAssetGatewayPowerMode(powerMode: string): ASSET_POWER_MODES | null {
  if (isNil(powerMode)) return null;
  if (powerMode === ASSET_POWER_MODES.SOLAR) return ASSET_POWER_MODES.SOLAR;
  else if (
    powerMode === ASSET_POWER_MODES.CABLE_CONNECTED ||
    powerMode === ASSET_POWER_MODES.THERMOKING_CABLE_CONNECTED
  ) return ASSET_POWER_MODES.CABLE_CONNECTED;
  else if (
    powerMode === ASSET_POWER_MODES.CABLE_POWERED ||
    powerMode === ASSET_POWER_MODES.THERMOKING_CABLE_POWERED
  ) return ASSET_POWER_MODES.CABLE_POWERED;
  else return null;
}

function isValidPowerModeforLowBattery(powerMode: string): boolean {
  return (
    powerMode === ASSET_POWER_MODES.SOLAR ||
    powerMode === ASSET_POWER_MODES.CABLE_CONNECTED ||
    powerMode === ASSET_POWER_MODES.THERMOKING_CABLE_CONNECTED ||
    powerMode === ASSET_POWER_MODES.CABLE_POWERED ||
    powerMode === ASSET_POWER_MODES.THERMOKING_CABLE_POWERED
  );
}

function assetGatewayBatteryStatus(
  AGBatteryStatus: IAGModelAndBattery,
  featureFlags: IKeyTypeBooleans,
  shouldGetFirstLableOnly: boolean = false,
): IBatteryStatus {
  let iconName: string = '';
  let phxIconColor: string;
  let phxIconSize: number = 0;
  let batteryLabel: string = '';
  let batteryStatusData: IBattery = null;
  let tooltipContents: IBatteryTooltip = null;
  let batteryAndTooltipData: IBatteryStatus = null;
  const { batteryCapacity, iconSize, powerMode } = AGBatteryStatus;
  let assetGatewayModel = AGBatteryStatus.assetGatewayModel;
  if (isNil(assetGatewayModel)) assetGatewayModel = AG_MODELS.AG_55;

  const isAGAssetGateway31LowBatteryChangesEnabled: boolean = featureFlags.isAGAssetGateway31LowBatteryChangesEnabled;
  const isAGAssetGateway55LowBatteryChangesEnabled: boolean = featureFlags.isAGAssetGateway55LowBatteryChangesEnabled;

  if (assetGatewayModel === AG_MODELS.AG_55) {
    if (isAGAssetGateway55LowBatteryChangesEnabled && isValidPowerModeforLowBattery(powerMode)) {
      batteryAndTooltipData = getNewAG55BatteryIconAndLabel(batteryCapacity, powerMode);
      tooltipContents = batteryAndTooltipData.tooltipData;
      iconName = batteryAndTooltipData.batteryData.iconPath;
      phxIconColor = batteryAndTooltipData.batteryData.iconColor;
      batteryLabel = getAG55BatteryStatusFirstLabel(batteryAndTooltipData.batteryData.label, shouldGetFirstLableOnly);
    } else {
      batteryStatusData = getAG55BatteryIconAndLabel(batteryCapacity);
      iconName = batteryStatusData.iconPath;
      phxIconColor = batteryStatusData.iconColor;
      batteryLabel = batteryStatusData.label;
    }
  } else if (assetGatewayModel === AG_MODELS.AG_31 && isAGAssetGateway31LowBatteryChangesEnabled) {
    batteryAndTooltipData = getAG31BatteryIconAndLabel(batteryCapacity);
    tooltipContents = batteryAndTooltipData.tooltipData;
    iconName = batteryAndTooltipData.batteryData.iconPath;
    phxIconColor = batteryAndTooltipData.batteryData.iconColor;
    batteryLabel = batteryAndTooltipData.batteryData.label;
  } else {
    iconName = null;
    phxIconColor = null;
    batteryLabel = null;
    tooltipContents = null;
  }

  if (!isNil(iconName)) {
    if (iconSize === ICON_SIZE_16) {
      phxIconSize = 16;
      if (iconName === BatteryIcons.BATTERY_LOW_BLINKING) iconName = `${FV_BATTERY_ICON_16}${iconName}`;
    }
    else {
      phxIconSize = 20;
      if (iconName === BatteryIcons.BATTERY_LOW_BLINKING) iconName = `${FV_BATTERY_ICON_20}${iconName}`;
    }
  }

  batteryAndTooltipData = {
    tooltipData: tooltipContents,
    batteryData: { iconPath: iconName, iconColor: phxIconColor, iconSize: phxIconSize, label: batteryLabel },
  };
  return batteryAndTooltipData;
}

export {
  isModelAG31,
  isModelAG55,
  BatteryIcons,
  BatteryStatuses,
  batteryLevelText,
  BATTERY_STATUSES,
  FV_BATTERY_ICON_16,
  FV_BATTERY_ICON_20,
  AG31BatteryLowText,
  EVERY_12_HOURS_TEXT,
  AG55LowBatteryLinks,
  BatteryTooltipTexts,
  AG31LowBatteryLinks,
  AG55LowBatteryMessages,
  AG31BatteryCriticalText,
  BatteryPercentageOfAG55,
  BatteryPercentageOfAG31,
  getAssetGatewayPowerMode,
  getOnlyAssetGatewayModel,
  isBatteryPercentageValid,
  assetGatewayBatteryStatus,
  ASSET_GATEWAY_BATTERY_TEXT,
  convertAG31UpdateRateValue,
  getAG31BatteryIconAndLabel,
  isAG55BatteryPercentageLow,
  isAG31BatteryPercentageLow,
  isAG55BatteryPercentageEmpty,
  getNewAG55BatteryIconAndLabel,
  isValidPowerModeforLowBattery,
  getAG55BatteryStatusFirstLabel,
  isAG55BatteryPercentageCritical,
  isAG31BatteryPercentageCritical,
};
